import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Tooltip, createStyles, rem} from '@mantine/core';
import type {MantineSize} from '@mantine/core';
import React from 'react';

import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Canonical union for the shared input chip. Structurally identical to
 * `ComponentKind` (new-prediction/newPredictionState) and `PredictionInputType`
 * (prediction-jobs/predictionInputs), so both call sites pass their own type
 * with no cast and neither module needs to change.
 */
export type InputChipType = 'aminoAcids' | 'smiles';

const TYPE_ICON: Record<InputChipType, BenchlingIconName> = {
  aminoAcids: 'protein',
  smiles: 'molecule',
};

// Tooltip-only fallback when an input has no name (listing, icon-only chips).
const TYPE_FALLBACK_LABEL: Record<InputChipType, string> = {
  aminoAcids: 'AA Sequence',
  smiles: 'Molecule',
};

interface InputChipProps {
  type: InputChipType;
  /** Visible chip text. Omit to render an icon-only chip (listing, unnamed inputs). */
  label?: string;
  /** Defaults to `sm` for compact listings; use `md` in editors like component cards. */
  size?: Extract<MantineSize, 'sm' | 'md'>;
  /** Show the label/type fallback tooltip when value is empty. Non-empty values always show a tooltip. */
  tooltip?: boolean;
  /** Full sequence/SMILES; shown untruncated in the tooltip when non-empty. */
  value?: string;
}

/**
 * The shared neutral-grey chip representing a prediction input's type (and, when known,
 * its name/label). Used on both the New Job inputs table and the Predictions listing so
 * the two stay visually identical.
 */
export default function InputChip({
  type,
  label,
  size = 'sm',
  tooltip = false,
  value,
}: InputChipProps): JSX.Element {
  const {classes} = useStyles({size});
  const badgeSize = size === 'md' ? 'lg' : 'sm';
  const hasValue = value != null && value.length > 0;
  const badge = (
    <Badge
      classNames={{inner: classes.inner, leftSection: classes.leftSection, root: classes.root}}
      color="gray"
      leftSection={<BenchlingIcon aria-hidden={true} className={classes.icon} icon={TYPE_ICON[type]} />}
      radius="sm"
      size={badgeSize}
      tt="none"
      variant="light"
      {...getTestProps({component: 'InputChip', element: 'root'})}
    >
      {label ?? ''}
    </Badge>
  );

  if (!hasValue && !tooltip) {
    return badge;
  }

  return (
    <Tooltip
      classNames={{tooltip: classes.tooltip}}
      label={hasValue ? value : (label ?? TYPE_FALLBACK_LABEL[type])}
      multiline={true}
      withinPortal={true}
      zIndex={ZINDEX_FLOATING_UI}
    >
      {badge}
    </Tooltip>
  );
}

const CHIP_MIN_HEIGHTS: Record<Extract<MantineSize, 'sm' | 'md'>, number> = {
  sm: 18,
  // Nucleus `md` badges are only 2px taller than `sm`, so editor chips map to `lg`.
  md: 24,
};

const useStyles = createStyles((theme, {size}: {size: Extract<MantineSize, 'sm' | 'md'>}) => ({
  // The Nucleus Badge theme fixes root height per size and clips `inner`, which crops descenders
  // (e.g. the "g" in "Ligand"). Let the chip grow to fit its text, matching StatusBadge.
  inner: {
    overflow: 'visible',
  },
  leftSection: {
    marginRight: size === 'md' ? 6 : undefined,
  },
  icon: {
    fontSize: size === 'md' ? 14 : 12,
  },
  root: {
    height: 'auto',
    minHeight: CHIP_MIN_HEIGHTS[size],
    ...(size === 'md' && {
      paddingLeft: theme.spacing.xs,
      paddingRight: theme.spacing.sm,
    }),
  },
  tooltip: {
    maxWidth: rem(420),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
}));
